import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { Modal } from './modal';

type Tone = 'danger' | 'default';

interface Props {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
  /** Font Awesome icon class; defaults per tone. */
  icon?: string;
  width?: number;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 6px 8px 2px;

  .head {
    display: flex;
    align-items: center;
    gap: 13px;
  }

  .title {
    font-size: 17px;
    font-weight: 700;
    color: rgb(255, 255, 255);
  }

  .message {
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1.55;
    color: rgba(176, 186, 209, 0.72);
  }
`;

const IconBadge = styled.span<{ $tone: Tone }>`
  display: inline-grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  font-size: 17px;
  background: ${({ $tone }): string =>
    $tone === 'danger' ? 'rgba(255, 80, 95, 0.14)' : 'rgba(78, 95, 253, 0.16)'};
  border: 1px solid
    ${({ $tone }): string =>
      $tone === 'danger' ? 'rgba(255, 80, 95, 0.4)' : 'rgba(120, 130, 255, 0.32)'};
  color: ${({ $tone }): string => ($tone === 'danger' ? 'rgb(255, 138, 147)' : 'rgb(159, 176, 255)')};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button<{ $variant: Tone | 'cancel' }>`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 18px;
  cursor: pointer;
  transition: all var(--transition);

  ${({ $variant }): string => {
    if ($variant === 'cancel') {
      return `
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: rgba(213, 221, 238, 0.92);
      `;
    }

    if ($variant === 'danger') {
      return `
        background: rgba(255, 80, 95, 0.16);
        border: 1px solid rgba(255, 80, 95, 0.45);
        color: rgb(255, 138, 147);
      `;
    }

    return `
      background: rgba(78, 95, 253, 0.16);
      border: 1px solid rgba(120, 130, 255, 0.4);
      color: rgb(159, 176, 255);
    `;
  }}

  &:hover,
  &:focus {
    outline: none;
    filter: brightness(1.1);
  }
`;

const defaultIcon = (tone: Tone): string =>
  tone === 'danger' ? 'fas fa-exclamation-triangle' : 'far fa-question-circle';

/*
 * Reusable confirmation prompt built on `Modal` (no header/close button). Renders
 * an icon + title + message with Cancel / Confirm actions in the modal footer.
 */
export const ConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  tone = 'default',
  icon,
  width = 440
}: Props): ReactElement | null => (
  <Modal
    open={open}
    onClose={onCancel}
    width={width}
    footer={
      <Actions>
        <Button type='button' $variant='cancel' onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button type='button' $variant={tone} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </Actions>
    }
  >
    <Content>
      <div className='head'>
        <IconBadge $tone={tone}>
          <i className={icon ?? defaultIcon(tone)} />
        </IconBadge>
        <div className='title'>{title}</div>
      </div>
      <div className='message'>{message}</div>
    </Content>
  </Modal>
);
